import styled, { css, keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";

export const Container = styled.div`
  position: relative;
  min-height: 100dvh;

  /* The global reset gives every element the system font: these screens are Inter */
  &,
  & * {
    font-family: Inter, sans-serif;
  }
  background:
    radial-gradient(60% 360px at 50% 0%, rgba(50, 188, 173, 0.12) 0%, rgba(50, 188, 173, 0) 70%),
    var(--color-neutral-darkness);
`;

export const Content = styled.main<{ $flow?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  max-width: 480px;
  padding: 4.5rem 0 4rem;
  margin: 0 auto;

  header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .method-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      color: var(--color-neutral-white);
      background: rgba(255, 255, 255, 0.06);
      border-radius: 16px;
    }

    h1 {
      margin-top: 1rem;
      font-size: 1.625rem;
      font-weight: 800;
      line-height: 2rem;
      letter-spacing: -0.02em;
      color: var(--color-neutral-white);
    }

    p {
      max-width: 400px;
      margin-top: 0.5rem;
      font-size: 0.9375rem;
      line-height: 1.375rem;
      color: rgba(255, 255, 255, 0.72);
    }
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 3.75rem 20px 3rem;

    header h1 {
      font-size: 1.375rem;
      line-height: 1.75rem;
    }
  }

  /* In the creation flow: right under the steps' header, the bar holds the actions */
  ${props =>
    props.$flow &&
    css`
      padding: 1.5rem 0 0;

      @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
        padding: 20px 20px 0;
      }
    `}
`;

/* What to pay: the amount, the code and the way to copy it */
export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  padding: 1.5rem;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 1.25rem;

  .amount {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    b {
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--color-neutral-white);
    }
  }

  .timer,
  .due {
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-150);
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    width: 100%;
  }

  .primary,
  .secondary {
    display: block;
    width: 100%;
    text-align: center;
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    font-weight: 800;
    border-radius: 100px;
    transition: filter 0.15s;

    &:hover {
      filter: brightness(1.1);
    }
  }

  .primary {
    color: var(--color-neutral-darkness);
    background: var(--color-brand-plus);
  }

  .secondary {
    color: var(--color-neutral-white);
    border: 1px solid var(--color-neutral-light);
  }

  .email {
    font-size: 0.75rem;
    color: var(--color-neutral-light);
    text-align: center;
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 1.25rem 1rem;

    .actions {
      grid-template-columns: 1fr;
    }
  }
`;

export const QrCode = styled.div<{ $expired: boolean }>`
  position: relative;
  width: 200px;
  height: 200px;
  overflow: hidden;
  border-radius: 12px;

  img {
    display: block;
    width: 100%;
    height: 100%;
    filter: ${props => (props.$expired ? "blur(4px) opacity(0.35)" : "none")};
  }

  .expired {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    font-size: 0.875rem;
    font-weight: 800;
    color: var(--color-neutral-white);
    text-align: center;
  }
`;

/* The code itself, readable but contained (the Pix one is long) */
export const Code = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;

  .label {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--color-neutral-150);
  }

  code {
    display: -webkit-box;
    padding: 0.75rem 0.875rem;
    overflow: hidden;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--color-neutral-white);
    word-break: break-all;
    background: var(--color-neutral-darkness);
    border: 1px solid var(--color-neutral-base);
    border-radius: 10px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  /* The boleto's line whole and centered, breaking only between its groups */
  code.boleto-line {
    display: block;
    font-size: 0.8125rem;
    line-height: 1.25rem;
    text-align: center;
    word-break: normal;
    -webkit-line-clamp: unset;
  }
`;

export const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 0.25rem;

  li {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: rgba(255, 255, 255, 0.8);
  }

  .number {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
    font-weight: 800;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 100%;
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 208, 13, 0.6);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 208, 13, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 208, 13, 0);
  }
`;

/* Waiting for the payment: a pulsing dot while the Pix is expected any second; calm for the boleto */
export const Status = styled.div<{ $calm?: boolean }>`
  display: flex;
  gap: 0.875rem;
  align-items: center;
  padding: 1rem 1.125rem;
  background: rgba(38, 42, 52, 0.6);
  border: 1px solid var(--color-neutral-base);
  border-radius: 1rem;

  .dot {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    background: #ffd00d;
    border-radius: 100%;
    ${props =>
      !props.$calm &&
      css`
        animation: ${pulse} 1.6s ease-out infinite;
      `}
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.1875rem;
    min-width: 0;

    b {
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--color-neutral-white);
    }

    span {
      font-size: 0.75rem;
      line-height: 1.125rem;
      color: var(--color-neutral-150);
    }
  }

  button {
    flex-shrink: 0;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    border: 1px solid var(--color-neutral-light);
    border-radius: 100px;

    &:disabled {
      cursor: progress;
      opacity: 0.6;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .dot {
      animation: none;
    }
  }
`;
